import { parseScanPayload } from './gs1';
import type { ScanKind } from './gs1';

/** Which device read the barcode. Carried for diagnostics and the on-screen indicator only. */
export type ScanSource = 'camera' | 'hardware';

/**
 * What `ScannerProvider` publishes and every scanning screen consumes. Both the camera and a
 * handheld produce this identical shape, which is what lets a page support a handheld without
 * containing a line of code about one.
 */
export type ScanResult = {
  gtin: string | null;
  serialNumber: string | null;
  lot: string | null;
  /** ISO calendar date (`YYYY-MM-DD`). */
  expiration: string | null;
  count: string | null;
  sscc: string | null;
  /** `raw` is a zone rack label, a tote label, anything that is not a GS1 barcode. */
  kind: ScanKind;
  /** The payload as received, minus transport framing. Zone and tote lookups match on this. */
  text: string;
  separatorsMissing: boolean;
  source: ScanSource;
};

/**
 * Turns a raw payload from any scanner into the result the app publishes, or null when there was
 * nothing to publish. Pure, so the contract every page depends on can be pinned by tests rather
 * than by rendering the provider.
 */
export const toScanResult = (raw: string, source: ScanSource): ScanResult | null => {
  if (!raw) return null;
  const payload = parseScanPayload(raw);
  if (!payload.text) return null;
  return {
    gtin: payload.gtin,
    serialNumber: payload.serialNumber,
    lot: payload.lot,
    expiration: payload.expiration,
    count: payload.count,
    sscc: payload.sscc,
    kind: payload.kind,
    text: payload.text,
    separatorsMissing: payload.separatorsMissing,
    source
  };
};
